/**
 * Hook that provides all state and handlers for the top-up balance split view panel.
 * Combines top-up amount selection, top-up execution, and redeem code functionality.
 */
import { selectTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { microsToDollars } from '@saaga/shared/utils/billing.util'
import { useCallback } from 'react'

import type { BillingBalance } from '@/components/billing/billing.interface'
import type { TopUpBalanceState } from '@/components/split-view/content-types/user-interaction/user-interaction-content.molecule'

import { useRedeemCode } from './use-redeem-code.hook'
import { useRedeemCodeInput } from './use-redeem-code-input.hook'
import { useTopUp } from './use-top-up.hook'
import { useTopUpAmount } from './use-top-up-amount.hook'

const DEFAULT_CURRENCY = 'USD'

const deriveBalance = (tenant: ReturnType<typeof selectTenant>): BillingBalance => ({
  amount: tenant ? microsToDollars(tenant.balance) : 0,
  currency: DEFAULT_CURRENCY,
})

export const useTopUpSplitView = (): TopUpBalanceState => {
  const tenant = useAppSelector(selectTenant)
  const balance = deriveBalance(tenant)

  const {
    selectedAmount,
    customAmount,
    handlePresetClick,
    handleCustomAmountChange,
    getCurrentAmount,
    isValidAmount,
    validationError,
  } = useTopUpAmount(true)

  const { topUp, isLoading: isTopUpLoading } = useTopUp()
  const { redeemCode, isLoading: isRedeemLoading } = useRedeemCode()
  const { code: redeemCodeValue, isValidCode, handleCodeChange, resetCode } = useRedeemCodeInput()

  const handleTopUp = useCallback((): void => {
    const amount = getCurrentAmount()
    if (amount && amount > 0) {
      void topUp(amount)
    }
  }, [getCurrentAmount, topUp])

  const handleRedeem = useCallback((): void => {
    if (!isValidCode) return
    void redeemCode(redeemCodeValue.trim()).then((success) => {
      if (success) resetCode()
    })
  }, [isValidCode, redeemCode, redeemCodeValue, resetCode])

  return {
    balance,
    selectedAmount,
    customAmount,
    isValidAmount,
    validationError,
    onPresetClick: handlePresetClick,
    onCustomAmountChange: handleCustomAmountChange,
    onTopUp: handleTopUp,
    isTopUpLoading,
    redeemCode: redeemCodeValue,
    isValidRedeemCode: isValidCode,
    onRedeemCodeChange: handleCodeChange,
    onRedeem: handleRedeem,
    isRedeemLoading,
  }
}
